<template>
    <UCard :ui="{body: 'p-0 sm:p-0'}" class="shrink-0">
        <UAlert variant="subtle" :color="validationErrors.length ? 'neutral' : 'success'">
            <template #description>
                <div v-if="valid && customer" class="grow text-lg text-base-content mb-2">
                    Faktura til <span class="text-xl font-bold">{{ customer.name }}</span> på kr <span class="text-xl font-bold">{{ formatCurrency(productLinesTotal) }}</span> med forfall
                    <span class="text-xl font-bold">{{ friendlyDate(invoiceData.dueDate) }}</span> klar til utsending!
                </div>
                <div class="flex gap-2 items-center flex-col @2xl:flex-row">
                    <div class="flex gap-2 order-2 @2xl:order-1 flex-col @2xl:flex-row w-full @2xl:w-fit">
                        <UButton
                            v-if="invoiceDraftUuid"
                            class="w-full @2xl:w-fit whitespace-nowrap"
                            color="neutral"
                            variant="outline"
                            @click="updateInvoiceDraft()"
                        >
                            Oppdater kladd
                        </UButton>
                        <UButton
                            class="w-full @2xl:w-fit whitespace-nowrap"
                            color="neutral"
                            variant="outline"
                            @click="saveInvoiceDraft()"
                        >
                            Lagre som ny kladd
                        </UButton>
                        <attachment-modal />
                    </div>
                    <div class="@2xl:grow @2xl:order-2 hidden @2xl:block"/>
                    <UPopover v-if="validationErrors.length" mode="hover" class="order-1 @2xl:order-3">
                        <div class="flex items-center gap-2 cursor-pointer underline decoration-dotted decoration-error hover:text-error" :title="validationErrors.length + ' problemer'">
                            <i class="i-ph-warning text-error inline-block"/>
                            <span class="text-sm">{{ validationErrors.length + ' problemer' }}</span>
                        </div>
                        <template #content>
                            <UAlert
                                color="error"
                                :title="validationErrors.length + ' problemer'"
                                variant="subtle"
                            >
                                <template #description>
                                    <ul>
                                        <li v-for="error in validationErrors" :key="error">- {{ cachedT(error) }}</li>
                                    </ul>
                                </template>
                            </UAlert>
                        </template>
                    </UPopover>

                    <div class="flex gap-2 flex-col @2xl:flex-row order-3 @2xl:order-4 w-full @2xl:w-fit">
                        <UButton
                            class="w-full @2xl:w-fit whitespace-nowrap"
                            :color="valid ? 'primary' : 'neutral'"
                            variant="outline"
                            :disabled="!valid || isWorking"
                            icon="i-mdi-file-pdf-box"
                            data-cy="create-invoice-pdf"
                            @click="createInvoice('None')"
                        >
                            Opprett PDF faktura
                        </UButton>
                        <UButton
                            v-if="customer?.contactInfo?.email"
                            class="w-full @2xl:w-fit whitespace-nowrap"
                            :color="valid ? 'primary' : 'neutral'"
                            :variant="valid ? 'solid' : 'outline'"
                            :disabled="!valid || isWorking"
                            icon="i-ph-paper-plane-tilt"
                            data-cy="create-invoice-email"
                            @click="createInvoice('Email')"
                        >
                            Send faktura på epost
                        </UButton>
                        <UButton
                            v-if="!isCreditNote && canReceiveInvoice"
                            class="w-full @2xl:w-fit whitespace-nowrap"
                            :color="valid ? 'primary' : 'neutral'"
                            :variant="valid ? 'solid' : 'outline'"
                            :disabled="!valid || isWorking"
                            icon="i-ph-paper-plane-tilt"
                            @click="createInvoice('Ehf')"
                        >
                            Send EHF-faktura
                        </UButton>
                        <UButton
                            v-if="isCreditNote && canReceiveCreditNote"
                            class="w-full @2xl:w-fit whitespace-nowrap"
                            :color="valid ? 'primary' : 'neutral'"
                            :variant="valid ? 'solid' : 'outline'"
                            :disabled="!valid || isWorking"
                            icon="i-mdi-email"
                            @click="createInvoice('Ehf')"
                        >
                            Send EHF
                        </UButton>
                    </div>
                </div>
            </template>
        </UAlert>
    </UCard>
</template>

<script setup lang="ts">

import {calculateTotal, useInvoiceCreator} from '@/invoice/invoice.service'
import {defaultInvoiceDraftName, useInvoiceDrafts} from '@/invoice/InvoiceCreator/invoice.draft.service'
import {Draft, DRAFT_TYPE} from '@/draft/drafts.service'
import emitter from '@/services/infrastructure/emitter'
import {INVOICE_CREATED, INVOICE_DRAFT_CREATED, INVOICE_DRAFT_UPDATED} from '@/types/invoice/InvoiceEvent'
import {notifySuccess} from '@/services/infrastructure/notification.service'
import {friendlyDate} from '@/dateUtils/dateUtils'
import {onMounted, onUnmounted} from 'vue'
import {useCachedT} from '@/services/useCachedI18n'
import {SendAs} from '@/invoice/types/CreateInvoiceCommand'
import hotkeys from 'hotkeys-js'
import {formatCurrency} from '@/services/formatter.service'
import AttachmentModal from '@/invoice/InvoiceCreator/AttachmentModal.vue'

const {cachedT} = useCachedT()


const {validationErrors, valid, customer, invoiceData, productLinesTotal, isWorking, create, isCreditNote, canReceiveInvoice, canReceiveCreditNote} = useInvoiceCreator()
const {saveDraft, invoiceDraftUuid, updateDraft, getInvoiceDraft} = useInvoiceDrafts()

const emit = defineEmits(['invoiceCreated'])

const updateInvoiceDraft = async () => {
    const uuid = invoiceDraftUuid.value
    if (!uuid) return false

    const name = getInvoiceDraft(uuid)!.name
    const totalAmount = calculateTotal(invoiceData.value.productLines)

    const invoiceDraft: Draft = {
        uuid: invoiceDraftUuid.value,
        name,
        type: DRAFT_TYPE.INVOICE,
        content: {...invoiceData.value, amount: totalAmount}
    }

    const response = await updateDraft(invoiceDraft)

    if (response.isRight()) {
        emitter.emit(INVOICE_DRAFT_UPDATED)
        notifySuccess({title: 'Oppdatert', description: `Kladden ${invoiceDraft.name} ble oppdatert.`})
    }
}

const saveInvoiceDraft = async () => {
    const totalAmount = calculateTotal(invoiceData.value.productLines)

    const invoiceDraft: Draft = {
        name: defaultInvoiceDraftName(),
        type: DRAFT_TYPE.INVOICE,
        content: {...invoiceData.value, amount: totalAmount}
    }

    const response = await saveDraft(invoiceDraft)

    if (response.uuid) {
        emitter.emit(INVOICE_DRAFT_CREATED)
        notifySuccess({title: 'Opprettet', description: `Kladden ${invoiceDraft.name} ble opprettet.`})
    }
}

// TODO: Convert SendsAs to enum (zod enum)? Using strings is scary 💀
const createInvoice = async (send: SendAs = 'None') => {
    await create(send)
    emitter.emit(INVOICE_CREATED)
    emit('invoiceCreated')
}

const activateCTA = () => {
    if (!isCreditNote.value && canReceiveInvoice.value) {
        createInvoice('Ehf')
    } else if (isCreditNote.value && canReceiveCreditNote.value) {
        createInvoice('Ehf')
    } else {
        createInvoice('Email')
    }
}

onMounted(() => {
    hotkeys('ctrl+shift+enter', activateCTA)
})

onUnmounted(() => {
    hotkeys.unbind('ctrl+shift+enter')
})


</script>

<style scoped>

</style>